import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// Revenue Per OGA - team-wise, not employee-wise (Requirement Document
// v1.0 S4.7's own Calculation Level), so this shows one row per OGD
// selling squad, not one per person.
const RevenuePerOgaDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/revenue-per-oga-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load Revenue Per OGA results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalEvaluated: res?.data?.totalEvaluated || 0, warningCount: res?.data?.warningCount || 0 });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Revenue Per OGA results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null || row.value === null ? -Infinity : row.threshold - row.value);
  const sortedRows = useMemo(() => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)), [rows]);

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    { field: 'teamName', headerName: 'Squad' },
    { field: 'activeMembers', headerName: 'Active Members', align: 'right' },
    { field: 'sumTarget', headerName: 'Total Target', align: 'right', render: (row) => `₹${Number(row.sumTarget).toLocaleString()}`, csvValue: (row) => row.sumTarget },
    {
      field: 'value',
      headerName: 'Avg Target Per OGA',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.value === null ? '-' : `₹${Number(row.value).toLocaleString()}`}
        </Typography>
      ),
      csvValue: (row) => (row.value === null ? '-' : row.value),
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => (row.threshold === null ? <Typography variant="body2" color="text.secondary">Not configured</Typography> : `< ₹${Number(row.threshold).toLocaleString()}`),
      csvValue: (row) => (row.threshold === null ? 'Not configured' : `< ₹${Number(row.threshold).toLocaleString()}`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Revenue Per OGA"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">Back to Warning Dashboard</Button>
          {canConfigure('ogd-team-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/ogd-team-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">Configure Thresholds</Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}
      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated} unit=" squads" />
      )}
      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          searchFields={['teamName']}
          emptyMessage="No active squad members this period."
          exportFileName="Revenue_Per_OGA"
          rowKey={(row) => row.wingId}
        />
      )}
    </WarningPage>
  );
};

export default RevenuePerOgaDashboard;
